<template>
  <div>
    <v-select
      v-model="model"
      :options="formattedOptions"
      :filterable="false"
      @search="onSearch"
      :multiple="isMultiple"
      :clearable="isMultiple"
      :disabled="disabled"
      ref="selectable"
      @input="onInput"
      label="label"
      :reduce="(item) => item.value"
      :placeholder="placeholder"
    >
      <template #list-footer>
        <li v-if="hasPagination" class="pagination">
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="!hasPrevPage"
            @click="prevPage"
          >
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="!hasNextPage"
            @click="nextPage"
          >
            <feather-icon icon="ChevronRightIcon" size="18" />
          </b-button>
        </li>
      </template>
    </v-select>
  </div>
</template>

<script>
import vSelect from "vue-select";
import { debounce } from "lodash";
import { BButton } from "bootstrap-vue";
import axios from "@axios";

export default {
  name: "SectorsSelectPaginated",
  components: {
    vSelect,
    BButton,
  },
  props: {
    value: {
      type: [Array, Number, null],
      default: null,
    },
    multiple: {
      type: [Boolean, Number],
      default: true,
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    initialOptions: {
      type: Array,
      default: () => [],
    },
    limit: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      model: null,
      search: "",
      currentPage: 1,
      meta: null,
      options: [],
      valueLabelCache: {},
    };
  },
  created() {
    this.syncModelFromValue();
  },
  mounted() {
    this.fillCacheFromInitialOptions();
    this.fetchData().then(() => this.fetchMissingLabels());
  },
  watch: {
    currentPage() {
      this.fetchData();
    },
    value() {
      this.syncModelFromValue();
      this.$nextTick(() => this.fetchMissingLabels());
    },
    initialOptions: {
      deep: true,
      handler() {
        this.syncModelFromValue();
        this.fillCacheFromInitialOptions();
      },
    },
    options() {
      this.fillCacheFromOptions();
    },
    model() {
      this.fillCacheFromOptions();
    },
  },
  computed: {
    isMultiple() {
      return this.multiple || this.multiple === 1;
    },
    selectedIds() {
      if (this.isMultiple && Array.isArray(this.model)) {
        return this.model;
      }
      if (!this.isMultiple && this.model != null) {
        return [this.model];
      }
      return [];
    },
    combinedOptions() {
      const fromApi = (this.options || []).map((item) => ({
        value: item.id,
        label: item.name != null ? String(item.name) : String(item.id),
      }));
      const selectedIdsSet = new Set(this.selectedIds);
      const optionsForSelected = this.selectedIds.map((id) => ({
        value: id,
        label: this.valueLabelCache[id] != null ? this.valueLabelCache[id] : String(id),
      }));
      const fromApiNotSelected = fromApi.filter((o) => !selectedIdsSet.has(o.value));
      return [...optionsForSelected, ...fromApiNotSelected];
    },
    formattedOptions() {
      return this.combinedOptions;
    },
    hasPagination() {
      return this.meta && this.meta.last_page > 1;
    },
    hasNextPage() {
      if (this.meta) {
        return this.meta.current_page < this.meta.last_page;
      }
      return false;
    },
    hasPrevPage() {
      if (this.meta) {
        return this.meta.current_page > 1;
      }
      return false;
    },
  },
  methods: {
    fillCacheFromInitialOptions() {
      const initial = Array.isArray(this.initialOptions) ? this.initialOptions : [];
      initial.forEach((o) => {
        if (o && o.value != null && o.label != null) {
          this.$set(this.valueLabelCache, o.value, o.label);
        }
      });
    },
    fillCacheFromOptions() {
      const opts = this.options || [];
      const ids = this.selectedIds || [];
      const idsSet = new Set(ids);
      opts.forEach((item) => {
        if (idsSet.has(item.id)) {
          const label = item.name != null ? String(item.name) : String(item.id);
          this.$set(this.valueLabelCache, item.id, label);
        }
      });
    },
    syncModelFromValue() {
      const val = this.value;
      if (this.isMultiple) {
        this.model = Array.isArray(val) ? val : [];
      } else {
        this.model = val != null ? val : null;
      }
    },
    onInput() {
      this.$emit("input", this.model);
    },
    fetchMissingLabels() {
      const ids = this.selectedIds.filter((id) => {
        const cached = this.valueLabelCache[id];
        return cached == null || cached === String(id);
      });
      if (ids.length === 0) return;
      ids.forEach((id) => {
        axios
          .get(`/v1/sector/${id}`)
          .then(({ data }) => {
            const item = (data && data.data) || data;
            if (item && item.id != null) {
              const label = item.name != null ? String(item.name) : String(item.id);
              this.$set(this.valueLabelCache, item.id, label);
            }
          })
          .catch(() => {});
      });
    },
    fetchData() {
      return new Promise((resolve, reject) => {
        const params = {
          max: this.limit,
          "page[n]": this.currentPage,
        };
        if (this.search && this.search.length) {
          params.q = this.search;
        }
        axios
          .get("/v1/sector", { params })
          .then(({ data }) => {
            this.meta = data.meta || null;
            this.options = data.data || [];
            this.fillCacheFromOptions();
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    onSearch: debounce(async function (query, loading) {
      if (query && query.length) {
        loading(true);
        this.search = query;
        this.currentPage = 1;
        await this.fetchData();
        loading(false);
      } else {
        this.currentPage = 1;
        this.search = "";
        await this.fetchData();
      }
    }, 500),
    nextPage() {
      if (this.hasNextPage) {
        this.currentPage += 1;
      }
    },
    prevPage() {
      if (this.hasPrevPage) {
        this.currentPage -= 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination {
  display: flex;
  justify-content: space-around;
  margin: 0.25rem 0.25rem 0;
}

.pagination button {
  flex-grow: 1;

  &:first-child {
    margin-right: 5px;
  }
}

.pagination button:hover {
  cursor: pointer;
}
</style>
